import {
  AdminMembershipServicesExpiryMode,
  AdminMembershipServicesStatus,
  CountryCode,
  Direction,
  OrderBy,
  PageInfo,
} from '@generated/schema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateRangeMode } from 'components/DateRange';

export interface States {
  dateRangeMode: DateRangeMode;
  filterCountry: CountryCode | string;
  filterExpiry: AdminMembershipServicesExpiryMode | string;
  filterMemberName: string;
  filterPackageName: string;
  filterPackageStatus: AdminMembershipServicesStatus;
  filterServiceId: string;
  isGridReady: boolean;
  isShowUtilityDrawer: boolean;
  listPackagesDatesSort: string;
  listPackagesEndCursor: string;
  listPackagesEndTime: string | null;
  listPackagesFirst: number | null;
  listPackagesOrderBy: OrderBy;
  listPackagesPageNumber: number;
  listPackagesPageSize: number;
  listPackagesRowIndex: number;
  listPackagesStartTime: string | null;
  pageInfo: PageInfo | null | undefined;
  recordLoaded: number;
}

const initialState: States = {
  dateRangeMode: 'today',
  filterCountry: '',
  filterExpiry: '',
  filterMemberName: '',
  filterPackageName: '',
  filterPackageStatus: AdminMembershipServicesStatus.Active,
  filterServiceId: '',
  isGridReady: false,
  isShowUtilityDrawer: false,
  listPackagesDatesSort: 'expiry-asc',
  listPackagesEndCursor: '',
  listPackagesEndTime: null,
  listPackagesFirst: 1000,
  listPackagesOrderBy: {
    direction: Direction.Asc,
    field: 'expiryDate',
  },
  listPackagesPageNumber: 0,
  listPackagesPageSize: 100,
  listPackagesRowIndex: -1,
  listPackagesStartTime: null,
  pageInfo: null,
  recordLoaded: 0,
};

//* 필터 필드를 이름으로 정의한다 — `filter` 접두사. clearFilters, Clear Filters
//* 버튼의 활성 판정, 그리고 Paused 뱃지가 전부 이 하나를 본다. 필터를 새로
//* 추가하면 세 곳이 저절로 따라오므로, 한 곳만 빠져 뱃지 수와 목록이 어긋나는
//* 사고가 애초에 불가능하다.
//* ponytail: 접두사 규약이 곧 계약이다. `filter`로 시작하지 않는 이름으로
//* 필터를 추가하면 세 곳 모두 놓친다 — 그때는 명시 목록으로 바꿔야 한다.
export const filterKeys = (
  Object.keys(initialState) as (keyof States)[]
).filter((key) => key.startsWith('filter'));

//* clearFilters가 비우는 대상. filterPackageStatus만 빠진다 — 호출부마다
//* 끝내는 값이 다르다(FilterClearAll은 Active, Paused 뱃지는 PAUSED).
export const clearedFilterKeys = filterKeys.filter(
  (key) => key !== 'filterPackageStatus',
);

//* Clear Filters 버튼의 활성 판정: 지울 게 하나도 없으면 비활성이다.
//* 기존 조건을 그대로 옮긴 것이다 — 필터 여섯 개가 전부 초기값이고
//* listPackagesDatesSort도 초기값('expiry-asc')일 때 참.
//* 정렬을 여기 남긴 이유: clearFilters는 정렬을 안 건드리지만 이 버튼은
//* 되돌리므로, 정렬만 바뀌어도 버튼은 켜져야 한다.
//* listPackagesOrderBy는 원래도 안 봤다 — FilterDateSort가 DatesSort와 항상
//* 같이 바꾸므로 중복이고, 넣으면 동작이 바뀐다.
export const isDefaultView = (state: States) =>
  state.listPackagesDatesSort === initialState.listPackagesDatesSort &&
  filterKeys.every((key) => state[key] === initialState[key]);

export const membersListPackagesSlice = createSlice({
  initialState,
  name: 'membersListPackages',
  reducers: {
    //* 목록을 좁히는 필터를 한 번에 비운다. FilterClearAll.tsx와 Title.tsx의
    //* Paused 뱃지가 둘 다 이걸 쓴다 — 호출부에서 따로 나열하면 필터가 하나
    //* 늘 때 한쪽만 고쳐지고, 그러면 뱃지 숫자와 클릭이 여는 목록이 어긋난다.
    //* 그게 바로 이 뱃지가 막으려던 실패다.
    //* 정렬은 여기서 안 건드린다 — 순서는 집합을 안 바꾸므로 수와 목록을
    //* 어긋나게 할 수 없고, 되돌리는 건 FilterClearAll의 몫이다.
    clearFilters: (state) => {
      clearedFilterKeys.forEach((key) => {
        //* 동적 키 대입이라 draft를 한 번 넓힌다. 값은 항상 초기값이다.
        (state as Record<string, unknown>)[key] = initialState[key];
      });
    },
    setDateRangeMode: (state, { payload }: PayloadAction<DateRangeMode>) => {
      state.dateRangeMode = payload;
    },
    setFilterCountry: (
      state,
      { payload }: PayloadAction<CountryCode | string>,
    ) => {
      state.filterCountry = payload;
    },
    setFilterExpiry: (
      state,
      { payload }: PayloadAction<AdminMembershipServicesExpiryMode | string>,
    ) => {
      state.filterExpiry = payload;
    },
    setFilterMemberName: (state, { payload }: PayloadAction<string>) => {
      state.filterMemberName = payload;
    },
    setFilterPackageName: (state, { payload }: PayloadAction<string>) => {
      state.filterPackageName = payload;
    },
    setFilterPackageStatus: (
      state,
      { payload }: PayloadAction<AdminMembershipServicesStatus>,
    ) => {
      state.filterPackageStatus = payload;
    },
    setFilterServiceId: (state, { payload }: PayloadAction<string>) => {
      state.filterServiceId = payload;
    },
    setIsGridReady: (state, { payload }: PayloadAction<boolean>) => {
      state.isGridReady = payload;
    },
    setIsShowUtilityDrawer: (state, { payload }: PayloadAction<boolean>) => {
      state.isShowUtilityDrawer = payload;
    },
    setListPackagesDatesSort: (state, { payload }: PayloadAction<string>) => {
      state.listPackagesDatesSort = payload;
    },
    setListPackagesEndCursor: (state, { payload }: PayloadAction<string>) => {
      state.listPackagesEndCursor = payload;
    },
    setListPackagesEndTime: (state, { payload }: PayloadAction<string>) => {
      state.listPackagesEndTime = payload;
    },
    setListPackagesFirst: (
      state,
      { payload }: PayloadAction<number | null>,
    ) => {
      state.listPackagesFirst = payload;
    },
    setListPackagesOrderBy: (state, { payload }: PayloadAction<OrderBy>) => {
      state.listPackagesOrderBy = payload;
    },
    setListPackagesPageNumber: (state, { payload }: PayloadAction<number>) => {
      state.listPackagesPageNumber = payload;
    },
    setListPackagesPageSize: (state, { payload }: PayloadAction<number>) => {
      state.listPackagesPageSize = payload;
    },
    setListPackagesRowIndex: (state, { payload }: PayloadAction<number>) => {
      state.listPackagesRowIndex = payload;
    },
    setListPackagesStartTime: (state, { payload }: PayloadAction<string>) => {
      state.listPackagesStartTime = payload;
    },
    setPageInfo: (state, { payload }: PayloadAction<PageInfo>) => {
      state.pageInfo = payload;
    },
    setRecordLoaded: (state, { payload }: PayloadAction<number>) => {
      state.recordLoaded = payload;
    },
  },
});

export default membersListPackagesSlice.reducer;

export const {
  clearFilters: clearFiltersAction,
  setDateRangeMode: setDateRangeModeAction,
  setFilterCountry: setFilterCountryAction,
  setFilterExpiry: setFilterExpiryAction,
  setFilterMemberName: setFilterMemberNameAction,
  setFilterPackageName: setFilterPackageNameAction,
  setFilterPackageStatus: setFilterPackageStatusAction,
  setFilterServiceId: setFilterServiceIdAction,
  setIsGridReady: setIsGridReadyAction,
  setIsShowUtilityDrawer: setIsShowUtilityDrawerAction,
  setListPackagesDatesSort: setListPackagesDatesSortAction,
  setListPackagesEndCursor: setListPackagesEndCursorAction,
  setListPackagesEndTime: setListPackagesEndTimeAction,
  setListPackagesFirst: setListPackagesFirstAction,
  setListPackagesOrderBy: setListPackagesOrderByAction,
  setListPackagesPageNumber: setListPackagesPageNumberAction,
  setListPackagesPageSize: setListPackagesPageSizeAction,
  setListPackagesRowIndex: setListPackagesRowIndexAction,
  setListPackagesStartTime: setListPackagesStartTimeAction,
  setPageInfo: setPageInfoAction,
  setRecordLoaded: setRecordLoadedAction,
} = membersListPackagesSlice.actions;
